import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const PortfolioForm = ({ formData, onUpdate, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRefs = useRef({});

  const portfolioCategories = [
    { id: 'engagement', label: 'Engagement Photos', icon: 'Heart' },
    { id: 'mehndi', label: 'Mehndi/Henna', icon: 'Palette' },
    { id: 'sangeet', label: 'Sangeet/Dance', icon: 'Music' },
    { id: 'ceremony', label: 'Wedding Ceremony', icon: 'Crown' },
    { id: 'reception', label: 'Reception', icon: 'PartyPopper' },
    { id: 'portraits', label: 'Couple Portraits', icon: 'Users' },
    { id: 'details', label: 'Detail Shots', icon: 'Camera' },
    { id: 'family', label: 'Family Photos', icon: 'Users2' }
  ];

  const mockPortfolioImages = [
    {
      id: 1,
      url: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400',
      category: 'ceremony',
      title: 'Traditional Hindu Wedding Ceremony'
    },
    {
      id: 2,
      url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=400',
      category: 'mehndi',
      title: 'Intricate Bridal Mehndi Design'
    },
    {
      id: 3,
      url: 'https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=400',
      category: 'sangeet',
      title: 'Sangeet Dance Performance'
    },
    {
      id: 4,
      url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400',
      category: 'reception',
      title: 'Wedding Reception Celebration'
    },
    {
      id: 5,
      url: 'https://images.unsplash.com/photo-1537633552985-df8429e8048b?w=400',
      category: 'portraits',
      title: 'Romantic Couple Portrait'
    },
    {
      id: 6,
      url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400',
      category: 'engagement',
      title: 'Engagement Photo Session'
    }
  ];

  const handleFileUpload = (files, category) => {
    const currentImages = formData.portfolioImages || [];
    const newImages = Array.from(files).map((file, index) => ({
      id: Date.now() + index,
      file,
      url: URL.createObjectURL(file),
      category,
      title: `${category} - ${file.name}`
    }));
    
    onUpdate({
      portfolioImages: [...currentImages, ...newImages]
    });
  };

  // Fixed file input trigger function
  const triggerFileInput = (categoryId) => {
    const fileInput = fileInputRefs.current[categoryId];
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files, category);
    }
  };

  const removeImage = (imageId) => {
    const currentImages = formData.portfolioImages || [];
    const updatedImages = currentImages.filter(img => img.id !== imageId);
    onUpdate({ portfolioImages: updatedImages });
  };

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    const totalImages = (formData.portfolioImages || []).length;
    if (totalImages < 3) {
      newErrors.portfolioImages = 'Please upload at least 3 portfolio images';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const getImagesByCategory = (category) => {
    return (formData.portfolioImages || []).filter(img => img.category === category);
  };

  return (
    <div className="space-y-8">
      {/* Portfolio Upload */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Portfolio & Gallery
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Showcase your best work to attract couples. Upload high-quality images organized by event type.
        </p>

        {/* Upload Instructions */}
        <div className="bg-surface-50 rounded-xl p-6 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="#8B4513" />
            <div>
              <h4 className="font-medium text-text-primary mb-2">Portfolio Guidelines</h4>
              <ul className="text-sm text-text-secondary font-caption space-y-1">
                <li>• Upload at least 3-10 high-quality images per category</li>
                <li>• Images should be at least 1200px wide for best quality</li>
                <li>• Accepted formats: JPG, PNG, WebP (max 5MB each)</li>
                <li>• Showcase your best work that represents your style</li>
                <li>• Include diverse cultural ceremonies if applicable</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Category Upload Sections */}
        <div className="space-y-6">
          {portfolioCategories.map((category) => {
            const categoryImages = getImagesByCategory(category.id);
            
            return (
              <div key={category.id} className="border border-border rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary-50 rounded-lg">
                    <Icon name={category.icon} size={16} color="#8B4513" />
                  </div>
                  <h4 className="font-heading font-semibold text-text-primary">{category.label}</h4>
                  <span className="text-sm text-text-muted font-data">
                    ({categoryImages.length} images)
                  </span>
                </div>

                {/* Upload Area */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={(e) => handleDrop(e, category.id)}
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-smooth ${
                    dragActive ? 'border-primary bg-primary-50' : 'border-border-light hover:border-primary'
                  }`}
                >
                  <Icon name="Upload" size={32} color="#6B5B4F" className="mx-auto mb-4" />
                  <p className="text-text-primary font-medium mb-2">
                    Drop images here or click to upload
                  </p>
                  <p className="text-sm text-text-secondary font-caption mb-4">
                    JPG, PNG, WebP up to 5MB each
                  </p>
                  
                  {/* Fixed hidden file input with proper ref */}
                  <input
                    ref={(el) => (fileInputRefs.current[category.id] = el)}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files, category.id)}
                    className="hidden"
                  />
                  
                  {/* Fixed Choose Files button */}
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconSize={16}
                    onClick={() => triggerFileInput(category.id)}
                    className="cursor-pointer"
                  >
                    Choose Files
                  </Button>
                </div>

                {/* Uploaded Images Grid */}
                {categoryImages.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
                    {categoryImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-surface-100">
                          <Image
                            src={image.url}
                            alt={image.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          onClick={() => removeImage(image.id)}
                          className="absolute top-2 right-2 w-6 h-6 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth"
                        >
                          <Icon name="X" size={12} color="currentColor" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 opacity-0 group-hover:opacity-100 transition-smooth">
                          <p className="text-xs font-caption truncate">{image.title}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {errors.portfolioImages && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.portfolioImages}</span>
          </p>
        )}
      </div>

      {/* Sample Work (Mock Data) */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Sample Portfolio
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Here's how your portfolio will appear to couples browsing your profile
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {mockPortfolioImages.map((image) => (
            <div key={image.id} className="group cursor-pointer">
              <div className="aspect-square rounded-lg overflow-hidden bg-surface-100 shadow-soft hover:shadow-medium transition-smooth">
                <Image
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-slow"
                />
              </div>
              <div className="mt-2">
                <p className="text-sm font-medium text-text-primary">{image.title}</p>
                <p className="text-xs text-text-muted font-caption capitalize">{image.category}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video Portfolio */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Video Portfolio (Optional)
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Add video links to showcase your work in motion
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              YouTube/Vimeo Video URL
            </label>
            <Input
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              value={formData.videoUrl1 || ''}
              onChange={(e) => handleInputChange('videoUrl1', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Additional Video URL
            </label>
            <Input
              type="url"
              placeholder="https://vimeo.com/..."
              value={formData.videoUrl2 || ''}
              onChange={(e) => handleInputChange('videoUrl2', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Client Testimonials */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Client Testimonials (Optional)
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Share what your clients say about working with you
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Testimonial 1
            </label>
            <textarea
              placeholder="Share a testimonial from a happy couple..."
              value={formData.testimonial1 || ''}
              onChange={(e) => handleInputChange('testimonial1', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
            <Input
              type="text"
              placeholder="Client name (optional)"
              value={formData.testimonial1Author || ''}
              onChange={(e) => handleInputChange('testimonial1Author', e.target.value)}
              className="mt-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Testimonial 2
            </label>
            <textarea
              placeholder="Share another testimonial..."
              value={formData.testimonial2 || ''}
              onChange={(e) => handleInputChange('testimonial2', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
            <Input
              type="text"
              placeholder="Client name (optional)"
              value={formData.testimonial2Author || ''}
              onChange={(e) => handleInputChange('testimonial2Author', e.target.value)}
              className="mt-2"
            />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
        >
          Back to Services
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={18}
        >
          Continue to Availability
        </Button>
      </div>
    </div>
  );
};

export default PortfolioForm;